import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ShieldCheck, Cpu, Mail, Phone, MapPin, Lock } from "lucide-react";

const nav = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Software" },
  { to: "/services", label: "Services" },
  { to: "/support", label: "24/7 Support" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header className="glass sticky top-0 z-50 border-b border-[#1E2F4D]" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <div className="w-9 h-9 rounded-lg bg-[#2563EB] flex items-center justify-center btn-glow">
              <Cpu className="w-5 h-5 text-white" />
            </div>
            <div className="leading-none">
              <span className="font-display font-extrabold text-lg text-white tracking-tight">REVONA</span>
              <span className="block text-[10px] font-mono tracking-[0.25em] text-cyan-400">SERVICES</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "text-white bg-[#172642]" : "text-slate-400 hover:text-white hover:bg-[#111C30]"
                  }`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/products"
              className="hidden sm:inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] btn-glow text-white text-sm font-semibold px-4 py-2.5 rounded-full"
              data-testid="header-shop-btn">
              <ShieldCheck className="w-4 h-4" /> Browse Software
            </Link>
            <button className="lg:hidden p-2 text-slate-300" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#1E2F4D] bg-[#0E1626] px-4 py-4 space-y-1" data-testid="mobile-menu">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"}
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "text-white bg-[#172642]" : "text-slate-300"}`}>
              {n.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-[#1E2F4D] bg-[#0A0E1A] mt-20" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg bg-[#2563EB] flex items-center justify-center">
                <Cpu className="w-4 h-4 text-white" />
              </div>
              <span className="font-display font-extrabold text-white">REVONA SERVICES</span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              Enterprise AI software, networking tools and 24/7 IT support. Based in Funchal, Madeira, Portugal.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Products</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/products?category=AI Software" className="hover:text-cyan-400">AI Software</Link></li>
              <li><Link to="/products?category=Network Tools" className="hover:text-cyan-400">Network Tools</Link></li>
              <li><Link to="/products?category=Computer Software" className="hover:text-cyan-400">Computer Software</Link></li>
              <li><Link to="/products?category=Digital Products" className="hover:text-cyan-400">Digital Products</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Company</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/services" className="hover:text-cyan-400">Services</Link></li>
              <li><Link to="/support" className="hover:text-cyan-400">24/7 Support</Link></li>
              <li><Link to="/legal/privacy" className="hover:text-cyan-400">Privacy Policy</Link></li>
              <li><Link to="/legal/terms" className="hover:text-cyan-400">Terms &amp; Conditions</Link></li>
              <li><Link to="/legal/refund" className="hover:text-cyan-400">Refund Policy</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Contact</h4>
            <ul className="space-y-2.5 text-sm text-slate-400">
              <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-cyan-400" /> revona.eu@gmail.com</li>
              <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-cyan-400" /> +351 913 286 138</li>
              <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-cyan-400" /> Funchal, Madeira, PT</li>
            </ul>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-[#1E2F4D] flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-slate-500 font-mono">© {new Date().getFullYear()} Revona Services · VAT/NIF 328 694 647</p>
          <Link to="/admin" className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-cyan-400" data-testid="admin-footer-link">
            <Lock className="w-3.5 h-3.5" /> Admin Portal
          </Link>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="App">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
